import { useState } from 'react'
import Swal from 'sweetalert2'
import multiLoginService from '../services/multiLoginService'
import { PERMISSIONS, ADMIN_TIER_PERMISSIONS, isOwnerOrSuperAdmin } from '../utils/permissions'
import useModalEnter from '../hooks/useModalEnter'

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: false,
})

/**
 * Edit-privileges modal for an executive (multi-login) account. Seeds checkbox
 * state from the stored permission map and PUTs the COMPLETE map back, so the
 * server-side merge behaves like a replace. Changes apply on the executive's
 * very next request — no re-login needed (authMiddleware re-reads the row).
 */
function ExecutivePermissionsModal({ executive, onClose, onSaved }) {
  // Admin-tier keys are hidden from non-owner callers (defence in depth — the
  // backend freezes them at their stored value anyway). List is empty today.
  const visiblePermissions = isOwnerOrSuperAdmin()
    ? PERMISSIONS
    : PERMISSIONS.filter((p) => !ADMIN_TIER_PERMISSIONS.includes(p.key))

  const [checked, setChecked] = useState(() => {
    const stored = executive.permissions || {}
    const initial = {}
    // Tolerate legacy rows stored with label keys (pre key-contract migration)
    PERMISSIONS.forEach((p) => { initial[p.key] = !!(stored[p.key] ?? stored[p.label]) })
    return initial
  })
  const [saving, setSaving] = useState(false)

  const isAllChecked = visiblePermissions.every((p) => checked[p.key])

  const toggle = (key) => {
    if (key === 'all') {
      const allOn = !isAllChecked
      setChecked((prev) => {
        const next = { ...prev }
        visiblePermissions.forEach((p) => { next[p.key] = allOn })
        return next
      })
    } else {
      setChecked((prev) => ({ ...prev, [key]: !prev[key] }))
    }
  }

  const handleSave = async () => {
    if (saving) return
    setSaving(true)
    try {
      const permissions = {}
      PERMISSIONS.forEach((p) => { permissions[p.key] = !!checked[p.key] })
      const res = await multiLoginService.update(executive.id, { permissions })
      Toast.fire({ icon: 'success', text: res?.message || 'Privileges updated' })
      onSaved()
    } catch (error) {
      Toast.fire({ icon: 'error', text: error.response?.data?.message || error.message })
    } finally {
      setSaving(false)
    }
  }

  const { modalClass, onBackdropClick } = useModalEnter(onClose)

  return (
    <div className={modalClass} tabIndex="-1" role="dialog" style={{ display: 'block', background: 'rgba(0,0,0,.5)' }} onClick={onBackdropClick}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Privileges — {executive.username}</h5>
            <button type="button" className="close" aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <div className="custom-control custom-checkbox mb-2">
              <input type="checkbox" className="custom-control-input" id="edit-privilege-all" checked={isAllChecked} onChange={() => toggle('all')} />
              <label className="custom-control-label" htmlFor="edit-privilege-all">All</label>
            </div>
            <div className="row">
              {visiblePermissions.map((p) => (
                <div className="col-md-4 col-sm-6" key={p.key}>
                  <div className="custom-control custom-checkbox mb-1">
                    <input type="checkbox" className="custom-control-input" id={`edit-privilege-${p.key}`} checked={!!checked[p.key]} onChange={() => toggle(p.key)} />
                    <label className="custom-control-label" htmlFor={`edit-privilege-${p.key}`}>{p.label}</label>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-success" disabled={saving} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExecutivePermissionsModal
